/**
 * Het bewakingstabblad: de conceptsignalen uit `sub_bron_signalen`, met per
 * signaal de brontekst ernaast en twee knoppen.
 *
 * Tot dit paneel bestond, las NIETS in `src/` die tabel — `grep -rn
 * sub_bron_signalen src/` gaf op 08-09-2026 nul treffers, terwijl de
 * edge-functie er wel in schrijft. De signalen liepen dus naar een plek waar
 * niemand keek.
 *
 * DE LEGE STAAT IS HIER HET BELANGRIJKSTE ONDERDEEL. Een paneel dat "geen
 * signalen, alles in orde" toont terwijl er nooit gemeten is, maakt exact de
 * fout die dit project op 08-09-2026 vier keer heeft gemaakt. Welke van de vijf
 * toestanden geldt, bepaalt `bepaalBewakingsToestand`; de woorden staan in
 * `toestandTekst`, allebei getoetst in `tests/subsidie/signalen.node.test.mjs`.
 *
 * Stand op 08-09-2026, ná de eerste echte run om 21:02: 7 signalen, alle zeven
 * concept, en 0 momentopnames — die run ging in de lichte modus en die slaat
 * laag 3 over. De pg_cron-job `subsidie-bewaking-ouderdom` heeft nul runs; hij
 * draait maandag 06:15. Het paneel staat vandaag dus op `open`.
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, CheckCircle2, Play, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  SIGNAAL_BEHEER_ROLLEN,
  toestandTekst,
  useSubsidieSignalen,
  type Besluit,
} from '@/hooks/useSubsidieSignalen';
import { SignaalKaart } from './SignaalKaart';

function tijdstipTekst(iso: string | null): string {
  if (!iso) return 'nooit';
  return new Date(iso).toLocaleString('nl-NL', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function SignalenPaneel({ jaar, onNaarBeheer }: { jaar: number; onNaarBeheer: () => void }) {
  const { hasRole } = useAuth();
  const {
    concepten, afgehandeld, toestand, aantalMomentopnames, laatsteOphaling,
    handelAf, draaiBewaking, isLoading, error, momentopnamesFout,
  } = useSubsidieSignalen(jaar);
  const [toonAfgehandeld, setToonAfgehandeld] = useState(false);

  /**
   * Mag deze gebruiker afhandelen? `sub_bron_signalen_select_team` laat drie
   * rollen lezen, `sub_bron_signalen_beheer` laat er twee schrijven (migratie
   * 063, gemeten in `pg_policies` op 08-09-2026). Zonder deze check zou een
   * crm_user twee knoppen zien die stil nul rijen raken.
   */
  const magAfhandelen = SIGNAAL_BEHEER_ROLLEN.some(rol => hasRole(rol));
  const tekst = toestandTekst(toestand);

  if (isLoading) return <p className="text-muted-foreground">Laden...</p>;

  if (error) {
    // Een mislukte lezing is geen lege lijst. Zonder deze tak zou een RLS-fout
    // of een ontbrekende tabel er identiek uitzien als "niets aan de hand".
    return (
      <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-900">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          De signalen konden niet worden geladen, dus dit scherm zegt niets over de stand van
          zaken. Melding: {error instanceof Error ? error.message : String(error)}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!magAfhandelen && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Je kunt hier meekijken maar niet afhandelen. De database laat bevestigen en afwijzen
            alleen toe voor de rollen admin en super_admin.
          </span>
        </div>
      )}

      {/* De stand van de bewaking zelf. `toestand` is 'onbekend' zodra de
          momentopnames niet te tellen waren — dan wordt "leeg" niet geduid. */}
      <Card className={tekst.vraagtAandacht ? 'border-amber-400 border-2' : ''}>
        <CardContent className="py-4 space-y-3">
          <div className="flex items-start gap-2">
            {tekst.vraagtAandacht
              ? <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
              : <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-green-600" />}
            <div className="space-y-1">
              <p className="font-semibold">{tekst.kop}</p>
              <p className="text-sm text-muted-foreground">{tekst.uitleg}</p>
              {momentopnamesFout != null && (
                <p className="text-sm text-muted-foreground">
                  Melding: {momentopnamesFout instanceof Error
                    ? momentopnamesFout.message
                    : String(momentopnamesFout)}
                </p>
              )}
            </div>
          </div>

          {/* De gemeten onderbouwing onder de bewering. Vier getallen, want
              alle vier zijn nodig om "leeg" te kunnen duiden. */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span>{concepten.length} open</span>
            <span>{afgehandeld.length} afgehandeld</span>
            <span>{aantalMomentopnames === null ? 'momentopnames onbekend' : `${aantalMomentopnames} momentopnames`}</span>
            <span>
              Laatste bronophaling: {aantalMomentopnames === null ? 'onbekend' : tijdstipTekst(laatsteOphaling)}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={!magAfhandelen || draaiBewaking.isPending}
              onClick={() => draaiBewaking.mutate()}
            >
              {draaiBewaking.isPending
                ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                : <Play className="h-4 w-4 mr-1" />}
              {draaiBewaking.isPending ? 'Bezig — haalt de RVO-pagina\'s op...' : 'Bewaking nu draaien'}
            </Button>
            {/* De duur staat erbij omdat hij echt lang is: de broncontrole mag
                tot 110 seconden aan RVO-pagina's besteden (LAAG3_DEADLINE_MS in
                de edge-functie). Zonder dit zinnetje ziet een terechte wachttijd
                eruit als een vastgelopen knop. */}
            <span className="text-xs text-muted-foreground">
              Loopt alle regelingen langs en haalt hun RVO-pagina op; dat duurt tot ongeveer twee
              minuten. Schrijft alleen signalen en momentopnames — nooit in de regelingen zelf.
            </span>
          </div>
        </CardContent>
      </Card>

      {concepten.map(signaal => (
        <SignaalKaart
          key={signaal.id}
          signaal={signaal}
          magAfhandelen={magAfhandelen}
          isBezig={handelAf.isPending}
          onNaarBeheer={onNaarBeheer}
          onAfhandelen={(invoer: { id: string; besluit: Besluit; notitie: string | null }) =>
            handelAf.mutate(invoer)}
        />
      ))}

      {afgehandeld.length > 0 && (
        <div className="space-y-2">
          <Button variant="ghost" size="sm" onClick={() => setToonAfgehandeld(!toonAfgehandeld)}>
            {toonAfgehandeld ? 'Verberg' : 'Toon'} {afgehandeld.length} afgehandelde
            {afgehandeld.length === 1 ? ' signaal' : ' signalen'}
          </Button>
          {toonAfgehandeld && afgehandeld.map(signaal => (
            <div key={signaal.id} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm">
              <Badge variant="outline">{signaal.status}</Badge>
              {signaal.regeling_naam && <Badge variant="secondary">{signaal.regeling_naam}</Badge>}
              <span className="text-muted-foreground">{signaal.titel}</span>
              {signaal.afhandel_notitie && (
                <span className="text-xs text-muted-foreground italic">— {signaal.afhandel_notitie}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
